import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import {
  listLocalSampleRows,
  getLocalSampleRow,
  putLocalSampleRow,
  deleteLocalSampleRow,
  revokeBlobUrl,
  rowToSample,
  type LocalSampleRow,
} from '@/lib/localSamples'

/**
 * Three-step sample upload hook (`useSampleUpload`) plus a
 * cached list query (`useSamples`). The upload flow:
 *
 *   1. POST /samples/upload-url → presigned PUT URL + sampleId
 *   2. PUT the file bytes directly to S3 via the URL
 *   3. POST /samples/:id/confirm → flips status=ready + drops
 *      TTL; returns the finalized sample row
 *
 * Errors at any step short-circuit and surface to the caller.
 * The placeholder DynamoDB row has a 24h TTL, so a browser that
 * dies between (2) and (3) cleans up automatically.
 */

/**
 * A single slice region on a sample — a named start/end pair in
 * seconds. The slicer editor writes an array of these into
 * `Sample.slices` and the sequencer can then play `sliceId` by
 * reading the region and passing (startSec, endSec-startSec) to
 * `AudioBufferSourceNode.start(when, offset, duration)`.
 *
 * We persist absolute seconds (not grid positions) so slices keep
 * working on samples without a BPM, and so a BPM change doesn't
 * silently relocate existing slices.
 */
export interface SampleSlice {
  /** Stable id — referenced from PadContent.sample.sliceId. */
  id: string
  startSec: number
  endSec: number
  /** Optional user-provided rename ("kick 1", "snare ghost", etc.). */
  label?: string
  /** Per-slice gain in dBFS. Multiplicative with velocity, the
   *  sample's own gainDb, and any pad-level gain. Defaults to
   *  0 dB when missing so pre-existing slice data stays neutral. */
  gainDb?: number
}

export interface Sample {
  id: string
  name: string
  contentType: string
  sizeBytes: number
  /**
   * S3 object key for cloud samples. Empty string for local
   * samples — they live in IndexedDB on this device only.
   */
  s3Key: string
  durationSec?: number
  createdAt: string
  updatedAt: string
  /** 1h presigned GET URL for cloud samples, or a `blob:` URL
   *  for local samples (generated on demand from IndexedDB). */
  playUrl: string
  playUrlExpiresAt?: number
  /**
   * Where this sample is stored. `cloud` (default) means the
   * audio bytes live in S3 and the row in DynamoDB. `local`
   * means the bytes live in IndexedDB on this device — no
   * cloud round-trip, no cross-device sync. The audio engine
   * doesn't care about the distinction (it fetches `playUrl`
   * either way), but the Slicer's save path branches: cloud
   * goes to PATCH, local goes to IndexedDB.
   */
  source?: 'cloud' | 'local'
  /**
   * Tempo in BPM — either user-entered (via the Bars input, exact
   * math: `bars × 4 × 60 / durationSec`) or auto-detected via the
   * onset-autocorrelation worker. Unset on samples uploaded before
   * the slicer existed.
   */
  bpm?: number
  /**
   * User's declaration of loop length in bars. When set alongside
   * `durationSec`, drives an exact BPM calculation that beats any
   * autodetector. Optional because one-shot samples have no loop
   * length to declare.
   */
  bars?: number
  /**
   * 0..1 confidence score, only set when `bpm` came from the
   * autodetector. Surfaced in the UI so users know whether to trust
   * the detected value or override it.
   */
  bpmConfidence?: number
  /**
   * Named slice regions. Missing or empty → pad hits play the full
   * sample (back-compat with pre-slicer pads).
   */
  slices?: SampleSlice[]
  /**
   * Non-destructive sample-start point, in seconds from the file's
   * actual t=0. Used by the slicer to crop leading silence: every
   * slice's `startSec` is constrained to be ≥ `trimStartSec`. The
   * audio engine plays slice regions verbatim (the sample-buffer
   * bytes aren't touched), so trim is purely a slicer-UI concept.
   * Missing or 0 → use the full file (legacy behaviour).
   */
  trimStartSec?: number
  /**
   * Non-destructive sample-end point, in seconds from t=0. Used to
   * crop trailing silence / room tone. Slices' `endSec` is bounded
   * by this. Missing or `>= durationSec` → use the full file.
   */
  trimEndSec?: number
  /**
   * Per-sample gain in dBFS. Applied multiplicatively with velocity
   * and any slice-level gain at playback time. Also written by the
   * Normalize action (`lib/sampleNormalize.ts`) so the change is
   * non-destructive — the underlying file bytes stay untouched.
   * Defaults to 0 dB when missing.
   */
  gainDb?: number
  /**
   * The linear peak value detected during the most recent Normalize
   * run (0..1). Surfaced in the UI so users can tell whether the
   * current `gainDb` came from normalize or manual adjustment.
   */
  normalizedPeak?: number
}

const SAMPLES_KEY = ['sequencerSamples']

export function useSamples(enabled = true) {
  return useQuery<Sample[]>({
    queryKey: SAMPLES_KEY,
    queryFn: async () => {
      // Normalize to [] for any non-array response (e.g. feature-
      // gated 403 surfaced as an object, or backend not yet
      // deployed). The component renders safely with an empty
      // list; the user sees no samples until the route is live.
      const raw = await apiClient.get<unknown>('/api/v1/sequencer/samples')
      return Array.isArray(raw) ? (raw as Sample[]) : []
    },
    enabled,
    // Short stale time so the picker reflects fresh state (new
    // uploads, deletions, renames from other tabs) without forcing
    // users to wait for UI churn. A 30s staleness window is tiny
    // compared to the bandwidth cost of the request — the samples
    // list is typically a dozen rows of JSON metadata.
    staleTime: 30 * 1000,
    // Keep the hourly refetch so presigned `playUrl`s refresh before
    // they expire (backend issues them with a 1h TTL). Any user
    // action that triggers re-use of a URL (pad hit) will also
    // trigger a fresh fetch if cached buffer is missing, so this
    // interval is a safety net for cases where the user leaves the
    // tab open for an hour without touching anything.
    refetchInterval: 55 * 60 * 1000,
    retry: false,
  })
}

interface UploadUrlResponse {
  sampleId: string
  uploadUrl: string
  s3Key: string
  contentType: string
}

interface ConfirmInput {
  durationSec?: number
}

interface UploadResult {
  sample: Sample
}

/**
 * Pick an appropriate audio MIME type for a File. Browsers
 * sometimes report `audio/x-wav` or an empty type — we normalise
 * to the three the backend accepts.
 */
function pickContentType(file: File): 'audio/wav' | 'audio/mpeg' | 'audio/ogg' {
  const t = file.type.toLowerCase()
  if (t.includes('mpeg') || t.includes('mp3')) return 'audio/mpeg'
  if (t.includes('ogg')) return 'audio/ogg'
  if (t.includes('wav')) return 'audio/wav'
  // Fallback based on extension — some browsers drop the MIME on
  // drag-and-drop.
  const lower = file.name.toLowerCase()
  if (lower.endsWith('.mp3')) return 'audio/mpeg'
  if (lower.endsWith('.ogg')) return 'audio/ogg'
  return 'audio/wav'
}

/**
 * Probe duration of an audio file client-side via an HTMLAudioElement.
 * Returns undefined on failure — the confirm step treats it as
 * optional, so a failed probe doesn't block the upload.
 *
 * The 3s timeout is critical for native (WKWebView): some WAV blobs
 * fire neither `loadedmetadata` nor `error`, leaving the inner
 * Promise pending forever. That hang would block the local-sample
 * Import mutation indefinitely (button stuck on "Importing…").
 * Resolving with undefined on timeout matches the docstring contract
 * — duration is best-effort, not load-bearing.
 */
async function probeDurationSec(file: File): Promise<number | undefined> {
  try {
    const url = URL.createObjectURL(file)
    const audio = new Audio()
    audio.preload = 'metadata'
    audio.src = url
    const result = await new Promise<number | undefined>(resolve => {
      // Mutable ref-object dodges `prefer-const` while still letting
      // `cleanup` (declared first) close over the timer id (set last).
      const timer: { id: number | undefined } = { id: undefined }
      const cleanup = () => {
        if (timer.id !== undefined) window.clearTimeout(timer.id)
        audio.removeEventListener('loadedmetadata', onMeta)
        audio.removeEventListener('error', onErr)
        URL.revokeObjectURL(url)
      }
      const onMeta = () => {
        const d = audio.duration
        cleanup()
        resolve(Number.isFinite(d) ? d : undefined)
      }
      const onErr = () => {
        cleanup()
        resolve(undefined)
      }
      audio.addEventListener('loadedmetadata', onMeta)
      audio.addEventListener('error', onErr)
      timer.id = window.setTimeout(() => {
        console.warn(
          '[probe-duration] timed out after 3s — proceeding without durationSec',
        )
        cleanup()
        resolve(undefined)
      }, 3000)
    })
    return result
  } catch {
    return undefined
  }
}

export function useSampleUpload() {
  const qc = useQueryClient()
  return useMutation<UploadResult, Error, { file: File; name?: string }>({
    mutationFn: async ({ file, name }) => {
      const contentType = pickContentType(file)
      // Step 1 — ask the backend for a presigned PUT URL. The
      // backend also writes the placeholder row with 24h TTL.
      const { sampleId, uploadUrl } = await apiClient.post<UploadUrlResponse>(
        '/api/v1/sequencer/samples/upload-url',
        {
          contentType,
          sizeBytes: file.size,
          name: name ?? file.name,
        },
      )

      // Step 2 — PUT the bytes to S3 directly. Fetch (not
      // apiClient) because this URL is S3-hosted, not our API;
      // skipping Authorization is intentional — the presigned
      // URL carries the auth.
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
      })
      if (!putRes.ok) {
        throw new Error(
          `Upload failed (${putRes.status}): ${await putRes.text().catch(() => '')}`,
        )
      }

      // Step 3 — probe duration + confirm.
      const durationSec = await probeDurationSec(file)
      const confirmed = await apiClient.post<Sample>(
        `/api/v1/sequencer/samples/${sampleId}/confirm`,
        { durationSec } satisfies ConfirmInput,
      )

      // Surface the confirm response so silent failures (missing
      // playUrl, missing id, anything else the backend forgot to
      // return) show up in the browser console instead of being
      // discovered by chasing why the slicer renders nothing.
      console.info('[sample-upload] confirmed', {
        id: confirmed.id,
        name: confirmed.name,
        hasPlayUrl: Boolean(confirmed.playUrl),
        durationSec: confirmed.durationSec,
      })

      return { sample: confirmed }
    },
    onSuccess: ({ sample }) => {
      // Synchronously fold the new sample into the cached list so
      // the PadEditor picker shows it *immediately* — without this,
      // users see a stale list (the query only refetches on
      // `invalidate` next time `useQuery` observers request data,
      // and a picker that's already open may not re-read). The
      // invalidateQueries below is belt-and-braces: it marks the
      // cache stale so the next mount refreshes from the server and
      // picks up durationSec or any other server-side enrichment.
      qc.setQueryData<Sample[]>(SAMPLES_KEY, prev => {
        const list = Array.isArray(prev) ? prev : []
        // Replace an existing row with the same id (e.g. if the
        // user re-uploaded to the same slot) so we don't end up
        // with duplicates.
        const deduped = list.filter(s => s.id !== sample.id)
        return [sample, ...deduped]
      })
      qc.invalidateQueries({ queryKey: SAMPLES_KEY })
    },
  })
}

export function useDeleteSample() {
  const qc = useQueryClient()
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: async id =>
      apiClient.delete<{ success: boolean }>(`/api/v1/sequencer/samples/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SAMPLES_KEY })
    },
  })
}

/**
 * Partial update of a sample row. Used by the Slicer to persist
 * BPM + slice metadata back to the backend without re-uploading
 * the audio. The server accepts any subset of these fields and
 * merges them into the existing DynamoDB row.
 */
export interface UpdateSampleInput {
  bpm?: number | null
  bars?: number | null
  bpmConfidence?: number | null
  slices?: SampleSlice[] | null
  /** Non-destructive sample-start point in seconds; null clears. */
  trimStartSec?: number | null
  /** Non-destructive sample-end point in seconds; null clears. */
  trimEndSec?: number | null
  /** Per-sample gain in dBFS. `null` clears back to 0 dB. */
  gainDb?: number | null
  /** Peak value from the last normalize run (0..1). `null` clears. */
  normalizedPeak?: number | null
}

export function useUpdateSample() {
  const qc = useQueryClient()
  return useMutation<Sample, Error, { id: string; updates: UpdateSampleInput }>(
    {
      mutationFn: ({ id, updates }) =>
        apiClient.patch<Sample>(`/api/v1/sequencer/samples/${id}`, updates),
      onSuccess: updated => {
        // Fold the updated row back into the cached list so the
        // PadEditor's slice dropdown reflects the new slice set
        // immediately — same pattern as the upload confirm path.
        qc.setQueryData<Sample[]>(SAMPLES_KEY, prev => {
          const list = Array.isArray(prev) ? prev : []
          return list.map(s => (s.id === updated.id ? updated : s))
        })
        qc.invalidateQueries({ queryKey: SAMPLES_KEY })
      },
    },
  )
}

// ── Local samples (IndexedDB-backed, per-device) ─────────────

const LOCAL_SAMPLES_KEY = ['localSamples']

/**
 * List local samples from IndexedDB. Same shape as `useSamples()`
 * so consumers can interchange them; the only divergence is
 * `Sample.source === 'local'` and `s3Key === ''`.
 *
 * `staleTime: Infinity` because IndexedDB is the source of truth —
 * nothing else can mutate it without going through our hooks, so
 * background refetches would be wasted work. Mutations explicitly
 * invalidate this key.
 */
export function useLocalSamples(enabled = true) {
  return useQuery<Sample[]>({
    queryKey: LOCAL_SAMPLES_KEY,
    queryFn: async () => {
      try {
        const rows = await listLocalSampleRows()
        // Distinctive log so the next "doesn't appear in dropdown"
        // session can confirm whether the read path even ran. If
        // this line is absent from devtools after Import, the
        // invalidate → refetch chain is broken; if it's present
        // with `count: 0`, the IndexedDB write didn't land.
        console.info('[local-list] read', { count: rows.length })
        return rows.map(rowToSample)
      } catch (err) {
        // Bumped to console.error so it shows up red in the
        // devtools console — easier to spot in noisy MIDI logs.
        console.error('[local-list] read failed', err)
        return []
      }
    },
    enabled,
    staleTime: Infinity,
    refetchInterval: false,
    retry: false,
  })
}

/**
 * Combined list — local first (newest local imports surface
 * above older cloud samples), then cloud. Used by every consumer
 * that doesn't care about source (PadEditor picker, Slicer
 * picker, SequencerPage prewarm).
 */
export function useAllSamples(enabled = true) {
  const cloud = useSamples(enabled)
  const local = useLocalSamples(enabled)
  const data = useMemo(
    () => [...(local.data ?? []), ...(cloud.data ?? [])],
    [cloud.data, local.data],
  )
  return {
    data,
    isLoading: cloud.isLoading || local.isLoading,
    isError: cloud.isError || local.isError,
  }
}

/**
 * Import a file from the user's device into the local-sample
 * store. Probes duration via the same HTMLAudioElement trick the
 * cloud upload uses, so the Slicer's "you need a duration before
 * chopping" gate passes the same way for local samples.
 */
export function useImportLocalSample() {
  const qc = useQueryClient()
  return useMutation<UploadResult, Error, { file: File; name?: string }>({
    mutationFn: async ({ file, name }) => {
      // Step-by-step logs so a hung import (native) or invisible
      // import (web) shows exactly where the chain stalled.
      console.info('[local-import] start', {
        name: name ?? file.name,
        sizeBytes: file.size,
        contentType: file.type || '(no type)',
      })
      const id = crypto.randomUUID()
      const contentType = pickContentType(file)
      console.info('[local-import] probing duration')
      const durationSec = await probeDurationSec(file)
      console.info('[local-import] probed', { durationSec, id })
      const now = new Date().toISOString()
      const row: LocalSampleRow = {
        id,
        name: name ?? file.name,
        contentType,
        sizeBytes: file.size,
        durationSec,
        bytes: file,
        createdAt: now,
        updatedAt: now,
      }
      await putLocalSampleRow(row)
      console.info('[local-import] persisted', { id })
      const sample = rowToSample(row)
      console.info('[local-import] done', {
        id: sample.id,
        name: sample.name,
        sizeBytes: sample.sizeBytes,
        durationSec: sample.durationSec,
        playUrlPresent: Boolean(sample.playUrl),
      })
      return { sample }
    },
    onError: err => {
      console.error('[local-import] failed', err)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LOCAL_SAMPLES_KEY })
      console.info('[local-import] invalidated cache')
    },
  })
}

/**
 * Slicer save path for local samples — mirrors `useUpdateSample`
 * but writes to IndexedDB. Accepts the same UpdateSampleInput
 * shape (bpm/bars/bpmConfidence/slices, with `null` meaning clear)
 * so the Slicer's existing save handler can branch on
 * `selectedSample.source` and hit the right mutation.
 */
export function useUpdateLocalSample() {
  const qc = useQueryClient()
  return useMutation<Sample, Error, { id: string; updates: UpdateSampleInput }>(
    {
      mutationFn: async ({ id, updates }) => {
        const row = await getLocalSampleRow(id)
        if (!row) throw new Error('Local sample not found')
        const next: LocalSampleRow = {
          ...row,
          updatedAt: new Date().toISOString(),
        }
        for (const key of [
          'bpm',
          'bars',
          'bpmConfidence',
          'slices',
          'trimStartSec',
          'trimEndSec',
          'gainDb',
          'normalizedPeak',
        ] as const) {
          if (!(key in updates)) continue
          const value = updates[key]
          if (value === null) {
            delete next[key]
          } else {
            // Type assertion is safe — UpdateSampleInput's value
            // types match LocalSampleRow's per-key types.
            ;(next as unknown as Record<string, unknown>)[key] = value
          }
        }
        await putLocalSampleRow(next)
        return rowToSample(next)
      },
      onSuccess: () => qc.invalidateQueries({ queryKey: LOCAL_SAMPLES_KEY }),
    },
  )
}

export function useDeleteLocalSample() {
  const qc = useQueryClient()
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: async id => {
      revokeBlobUrl(id)
      await deleteLocalSampleRow(id)
      return { success: true }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LOCAL_SAMPLES_KEY }),
  })
}
